import { MakeLogicType, connect, kea, path, props } from 'kea'
import { forms } from 'kea-forms'
import type { DeepPartial, DeepPartialMap, FieldName, ValidationErrorType } from 'kea-forms'

import api from 'lib/api'
import { integrationsLogic } from 'lib/integrations/integrationsLogic'
import { lemonToast } from 'lib/lemon-ui/LemonToast'

import { IntegrationType } from '~/types'

export interface TwilioSetupModalLogicProps {
    integration?: IntegrationType | null
    onComplete: (integrationId?: number) => void
    onClose: () => void
}

export interface TwilioFormType {
    accountSid: string
    authToken: string
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface twilioSetupModalLogicValues {
    isTwilioIntegrationSubmitting: boolean
    isTwilioIntegrationValid: boolean
    showTwilioIntegrationErrors: boolean
    twilioIntegration: {
        accountSid: string
        authToken: string
    }
    twilioIntegrationAllErrors: Record<string, any>
    twilioIntegrationChanged: boolean
    twilioIntegrationErrors: DeepPartialMap<
        {
            accountSid: string
            authToken: string
        },
        ValidationErrorType
    >
    twilioIntegrationHasErrors: boolean
    twilioIntegrationManualErrors: Record<string, any>
    twilioIntegrationTouched: boolean
    twilioIntegrationTouches: Record<string, boolean>
    twilioIntegrationValidationErrors: DeepPartialMap<
        {
            accountSid: string
            authToken: string
        },
        ValidationErrorType
    >
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface twilioSetupModalLogicActions {
    loadIntegrations: () => any // integrationsLogic
    resetTwilioIntegration: (values?: { accountSid: string; authToken: string }) => {
        values?: {
            accountSid: string
            authToken: string
        }
    }
    setTwilioIntegrationManualErrors: (errors: Record<string, any>) => {
        errors: Record<string, any>
    }
    setTwilioIntegrationValue: (
        key: FieldName,
        value: any
    ) => {
        name: FieldName
        value: any
    }
    setTwilioIntegrationValues: (
        values: DeepPartial<{
            accountSid: string
            authToken: string
        }>
    ) => {
        values: DeepPartial<{
            accountSid: string
            authToken: string
        }>
    }
    submitTwilioIntegration: () => {
        value: boolean
    }
    submitTwilioIntegrationFailure: (
        error: Error,
        errors: Record<string, any>
    ) => {
        error: Error
        errors: Record<string, any>
    }
    submitTwilioIntegrationRequest: (twilioIntegration: { accountSid: string; authToken: string }) => {
        twilioIntegration: {
            accountSid: string
            authToken: string
        }
    }
    submitTwilioIntegrationSuccess: (twilioIntegration: { accountSid: string; authToken: string }) => {
        twilioIntegration: {
            accountSid: string
            authToken: string
        }
    }
    touchTwilioIntegrationField: (key: string) => {
        key: string
    }
}

export type twilioSetupModalLogicType = MakeLogicType<
    twilioSetupModalLogicValues,
    twilioSetupModalLogicActions,
    TwilioSetupModalLogicProps
>

export const twilioSetupModalLogic = kea<twilioSetupModalLogicType>([
    path(['products', 'workflows', 'frontend', 'TwilioSetup', 'twilioSetupModalLogic']),
    props({} as TwilioSetupModalLogicProps),
    connect(() => ({
        actions: [integrationsLogic, ['loadIntegrations']],
    })),
    forms(({ props, actions, values }) => ({
        twilioIntegration: {
            defaults: {
                accountSid: '',
                authToken: '',
            },
            errors: ({ accountSid, authToken }) => ({
                accountSid: accountSid.trim() ? undefined : 'Account SID is required',
                authToken: authToken.trim() ? undefined : 'Auth Token is required',
            }),
            submit: async () => {
                try {
                    const integration = await api.integrations.create({
                        kind: 'twilio',
                        config: {
                            account_sid: values.twilioIntegration.accountSid,
                            auth_token: values.twilioIntegration.authToken,
                        },
                    })
                    actions.loadIntegrations()
                    lemonToast.success('Twilio channel created successfully!')
                    props.onComplete(integration.id)
                } catch (error: any) {
                    lemonToast.error(error.detail || 'Failed to create Twilio channel')
                    throw error
                }
            },
        },
    })),
])
